import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { partnerDdScoringApi } from '../../../api/partner-dd-scoring';
import { ApiError } from '../../../api/client';
import { PageHeader } from '../../../components/PageHeader';
import { SectionHeader } from '../../../components/SectionHeader';
import { StatusBadge } from '../../../components/StatusBadge';
import { useToast } from '../../../components/Toast';

/**
 * System Setup - Partner Due Diligence Scoring (spec section 48).
 * Deliberately a SEPARATE page under Administration, reachable only
 * through its own sidebar entry gated to
 * partner.due_diligence.model.view/.manage/.activate - never linked
 * from the operational Partner Workspace, and never reachable by
 * whoever merely has partner.due_diligence.view/.manage for day-to-day
 * case work (explicit direction: partner-facing roles should not see
 * scoring configuration at all).
 */
export function PartnerDdScoringSetupPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);

  const modelsQuery = useQuery({ queryKey: ['dd-scoring-models-admin'], queryFn: () => partnerDdScoringApi.listModels() });
  const modelDetailQuery = useQuery({ queryKey: ['dd-scoring-model-detail-admin', selectedModelId], queryFn: () => partnerDdScoringApi.getModel(selectedModelId as number), enabled: !!selectedModelId });
  const validationQuery = useQuery({ queryKey: ['dd-scoring-model-validate', selectedModelId], queryFn: () => partnerDdScoringApi.validateModel(selectedModelId as number), enabled: !!selectedModelId });

  const { register, handleSubmit, reset } = useForm<{ modelCode: string; version: string; name: string; description: string }>();
  const createMutation = useMutation({
    mutationFn: (values: { modelCode: string; version: string; name: string; description: string }) => partnerDdScoringApi.createModel(values),
    onSuccess: () => { showSuccess('Model created as draft.'); queryClient.invalidateQueries({ queryKey: ['dd-scoring-models-admin'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create model.'),
  });

  const duplicateMutation = useMutation({
    mutationFn: ({ id, version }: { id: number; version: string }) => partnerDdScoringApi.duplicateModel(id, version),
    onSuccess: () => { showSuccess('Model duplicated as a new draft version.'); queryClient.invalidateQueries({ queryKey: ['dd-scoring-models-admin'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to duplicate model.'),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => partnerDdScoringApi.activateModel(id),
    onSuccess: () => { showSuccess('Model activated.'); queryClient.invalidateQueries({ queryKey: ['dd-scoring-models-admin'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to activate - check validation below first.'),
  });
  const deactivateMutation = useMutation({
    mutationFn: (id: number) => partnerDdScoringApi.deactivateModel(id),
    onSuccess: () => { showSuccess('Model deactivated.'); queryClient.invalidateQueries({ queryKey: ['dd-scoring-models-admin'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate.'),
  });

  const detail = modelDetailQuery.data?.data;
  const totalWeight = detail?.criteria.reduce((sum, c) => sum + Number(c.weight_percent), 0) ?? 0;

  return (
    <div className="page">
      <PageHeader title="Partner Due Diligence Scoring" description="System configuration for how partner due diligence is scored, risk-banded, and routed for approval. Not visible to staff managing individual partner records." />

      <div className="panel">
        <SectionHeader title="Scoring Models" />
        <div className="panel-body">
          {modelsQuery.data && (
            <table className="data-table">
              <thead><tr><th>Code</th><th>Version</th><th>Name</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {modelsQuery.data.data.map((m) => (
                  <tr key={m.id}>
                    <td>{m.model_code}</td><td>{m.version}</td><td>{m.name}</td>
                    <td><StatusBadge status={m.status} /></td>
                    <td className="row-actions">
                      <button onClick={() => setSelectedModelId(m.id)}>View / Validate</button>
                      {m.status !== 'active' && <button onClick={() => activateMutation.mutate(m.id)} disabled={activateMutation.isPending}>Activate</button>}
                      {m.status === 'active' && <button onClick={() => deactivateMutation.mutate(m.id)} disabled={deactivateMutation.isPending}>Deactivate</button>}
                      <button onClick={() => { const version = window.prompt('New version identifier (e.g. 1.1):'); if (version) duplicateMutation.mutate({ id: m.id, version }); }}>Duplicate as New Version</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3>Create a New Model</h3>
          <form className="inline-form" onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>Model Code<input {...register('modelCode', { required: true })} placeholder="e.g. STANDARD" /></label>
            <label>Version<input {...register('version', { required: true })} placeholder="e.g. 1.0" /></label>
            <label>Name<input {...register('name', { required: true })} /></label>
            <label>Description<input {...register('description')} /></label>
            <button type="submit" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create Draft Model'}</button>
          </form>
        </div>
      </div>

      {detail && (
        <div className="panel">
          <SectionHeader title={`${detail.model.name} - Detail`} />
          <div className="panel-body">
            {validationQuery.data && (
              <div style={{ color: validationQuery.data.data.valid ? 'var(--color-success)' : 'var(--color-danger)' }}>
                {validationQuery.data.data.valid ? (
                  <p>Criteria Weight: 100% ✓ · Score Scale: Valid ✓ · Risk Bands: Valid ✓ · Approval Rules: Valid ✓</p>
                ) : (
                  <ul>{validationQuery.data.data.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                )}
              </div>
            )}

            <h3>Criteria (total weight: {totalWeight.toFixed(2)}%)</h3>
            <table className="data-table">
              <thead><tr><th>Code</th><th>Name</th><th>Weight</th><th>Mandatory</th><th>Evidence Required</th></tr></thead>
              <tbody>
                {detail.criteria.map((c) => (
                  <tr key={c.id}><td>{c.criterion_code}</td><td>{c.name}</td><td>{c.weight_percent}%</td><td>{Number(c.is_mandatory) === 1 ? 'Yes' : 'No'}</td><td>{Number(c.evidence_required) === 1 ? 'Yes' : 'No'}</td></tr>
                ))}
              </tbody>
            </table>

            <h3>Score Scale</h3>
            <table className="data-table">
              <thead><tr><th>Value</th><th>Label</th><th>Description</th></tr></thead>
              <tbody>{detail.scale.map((s) => <tr key={s.score_value}><td>{s.score_value}</td><td>{s.label}</td><td>{s.description}</td></tr>)}</tbody>
            </table>

            <h3>Risk Bands</h3>
            <table className="data-table">
              <thead><tr><th>Risk</th><th>Min</th><th>Max</th></tr></thead>
              <tbody>{detail.riskBands.map((b) => <tr key={b.risk_label}><td>{b.risk_label}</td><td>{b.min_score}</td><td>{b.max_score}</td></tr>)}</tbody>
            </table>

            <h3>Override Rules</h3>
            <table className="data-table">
              <thead><tr><th>Code</th><th>Label</th><th>Forces Risk</th></tr></thead>
              <tbody>{detail.overrideRules.map((o) => <tr key={o.rule_code}><td>{o.rule_code}</td><td>{o.label}</td><td>{o.forces_risk_label}</td></tr>)}</tbody>
            </table>

            <h3>Approval Rules</h3>
            <table className="data-table">
              <thead><tr><th>Risk</th><th>Approver Role</th><th>Enhanced DD</th><th>Blocks Approval</th></tr></thead>
              <tbody>
                {detail.approvalRules.map((a) => (
                  <tr key={a.risk_label}><td>{a.risk_label}</td><td>{a.approver_role ?? '-'}</td><td>{Number(a.requires_enhanced_dd) === 1 ? 'Yes' : 'No'}</td><td>{Number(a.blocks_approval) === 1 ? 'Yes' : 'No'}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
